import React, { useState, useEffect } from 'react';
import { Search, MapPin, Briefcase, Filter, WifiOff, Plus, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/common/Button';
import StateSearch from '../components/common/StateSearch';
import JobSeekerGrid from '../components/jobs/seeker/JobSeekerGrid';
import CreateProfileModal from '../components/jobs/seeker/CreateProfileModal';
import EditProfileModal from '../components/jobs/seeker/EditProfileModal';
import { useJobSeekerProfiles } from '../hooks/useJobSeekerProfiles';
import { updateJobSeekerProfile as updateFirebaseProfile, deleteJobSeekerProfile as deleteFirebaseProfile } from '../lib/firebase/jobSeekers';
import { addJobSeekerProfile as addSupabaseProfile, deleteJobSeekerProfile as deleteSupabaseProfile } from '../lib/supabase/jobSeekers';
import { useScrollPosition } from '../hooks/useScrollPosition';
import type { JobSeekerProfile, JobSeekerProfileData } from '../types/profiles';
import type { USState } from '../utils/states';

const JOB_TYPES = [
  'Full-time',
  'Part-time',
  'Seasonal',
  'Temporary'
] as const;

export default function JobSeekers() {
  const { user, firebaseUser } = useAuth();
  const { profiles, loading, error, refreshProfiles } = useJobSeekerProfiles();
  const [filteredProfiles, setFilteredProfiles] = useState<JobSeekerProfile[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<JobSeekerProfile | null>(null);
  const [selectedState, setSelectedState] = useState<USState | null>(() => {
    const saved = sessionStorage.getItem('jobSeekersSelectedState');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedJobType, setSelectedJobType] = useState<string>(() => {
    return sessionStorage.getItem('jobSeekersSelectedType') || '';
  });
  const { restoreScrollPosition } = useScrollPosition({
    key: 'jobSeekersListScroll',
    storage: 'session'
  });

  useEffect(() => {
    if (!loading) {
      restoreScrollPosition();
    }
  }, [loading, restoreScrollPosition]);

  useEffect(() => {
    sessionStorage.setItem('jobSeekersSelectedState', JSON.stringify(selectedState));
    sessionStorage.setItem('jobSeekersSelectedType', selectedJobType);
  }, [selectedState, selectedJobType]);

  useEffect(() => {
    let filtered = profiles;

    if (selectedState) {
      filtered = filtered.filter(profile =>
        profile.locations.some(location =>
          location.toLowerCase().includes(selectedState.name.toLowerCase()) ||
          location.toLowerCase().includes(selectedState.abbreviation.toLowerCase())
        )
      );
    }

    if (selectedJobType) {
      filtered = filtered.filter(profile =>
        profile.jobTypes.some(type =>
          type.toLowerCase() === selectedJobType.toLowerCase()
        )
      );
    }

    setFilteredProfiles(filtered);
  }, [selectedState, selectedJobType, profiles]);

  const clearFilters = () => {
    setSelectedState(null);
    setSelectedJobType('');
    sessionStorage.removeItem('jobSeekersSelectedState');
    sessionStorage.removeItem('jobSeekersSelectedType');
  };

  useEffect(() => {
    const pendingApplication = localStorage.getItem('pendingJobApplication');
    
    if (pendingApplication && !user) {
      try {
        const parsed = JSON.parse(pendingApplication);
        const signupUrl = `/signup?email=${encodeURIComponent(parsed.email)}&job=${encodeURIComponent(parsed.jobTitle)}`;
        window.location.href = signupUrl;
        return;
      } catch (e) {
        localStorage.removeItem('pendingJobApplication');
      }
    }

    const paymentInfo = localStorage.getItem('paymentCompleted');
    
    if (paymentInfo && user?.id) {
      try {
        const parsed = JSON.parse(paymentInfo);
        if (parsed.type === 'job-seeker' && user?.id) {
          setShowCreateModal(true);
          localStorage.removeItem('paymentCompleted');
        }
      } catch (e) {
        localStorage.removeItem('paymentCompleted');
      }
    }

    if (pendingApplication && user?.id) {
      try {
        const parsed = JSON.parse(pendingApplication);
        setShowCreateModal(true);
        localStorage.removeItem('pendingJobApplication');
      } catch (e) {
        localStorage.removeItem('pendingJobApplication');
      }
    }
  }, [user, profiles]);

  const handleCreateProfile = async (profileData: JobSeekerProfileData): Promise<string> => {
    if (!user?.id) {
      throw new Error('Please sign in to create a profile');
    }

    try {
      const { id, error } = await addSupabaseProfile(user.id, profileData);
      if (error) throw error;
      if (!id) throw new Error('Profile was created but no ID was returned');

      await refreshProfiles();
      return id;
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateProfile = async (profileData: JobSeekerProfileData) => {
    if (!editingProfile) return;

    try {
      const { error } = await updateFirebaseProfile(editingProfile.id, profileData);
      if (error) throw error;
      refreshProfiles();
      setEditingProfile(null);
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) {
      return;
    }

    try {
      // Try deleting from both systems since we don't know which one the profile is in
      const [firebaseResult, supabaseResult] = await Promise.all([
        deleteFirebaseProfile(profileId),
        deleteSupabaseProfile(profileId)
      ]);
      
      // If both fail, show error
      if (firebaseResult.error && supabaseResult.error) {
        throw new Error('Failed to delete profile from both systems');
      }
      
      refreshProfiles();
    } catch (err) {
      alert((err as Error).message);
    }
  };

  // Check if current user has existing profile (only for authenticated users)
  const currentUser = user || firebaseUser;
  const userHasProfile = currentUser && profiles.some(p => p.userId === currentUser.id || p.userId === currentUser.uid);

  return (
    <>
      <Helmet>
        <title>Job Seekers | RanchWorkUSA</title>
        <meta 
          name="description" 
          content="Browse skilled ranch workers looking for opportunities. Create your professional profile to get discovered by employers."
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Job Seekers</h1>
            <p className="mt-2 text-gray-600">
              Find skilled ranch workers {currentUser ? 'or create your professional profile' : ''}
            </p>
          </div>
          {currentUser && !userHasProfile && (
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setShowCreateModal(true)}
            >
              Create Profile
            </Button>
          )}
        </div>

        <div className="mb-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 max-w-4xl mx-auto">
            <div className="flex-1">
              <StateSearch
                onStateSelect={setSelectedState}
                selectedState={selectedState}
              />
            </div>
            <div className="flex-1">
              <select
                value={selectedJobType}
                onChange={(e) => setSelectedJobType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">All Job Types</option>
                {JOB_TYPES.map(type => (
                  <option key={type} value={type.toLowerCase()}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {(selectedState || selectedJobType) && (
            <div className="flex flex-wrap items-center gap-3 max-w-4xl mx-auto">
              <span className="text-sm text-gray-600">Active filters:</span>
              {selectedState && (
                <button
                  onClick={() => setSelectedState(null)}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm hover:bg-green-200"
                >
                  {selectedState.name}
                  <X className="h-3 w-3" />
                </button>
              )}
              {selectedJobType && (
                <button
                  onClick={() => setSelectedJobType('')}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm hover:bg-green-200"
                >
                  {JOB_TYPES.find(t => t.toLowerCase() === selectedJobType)}
                  <X className="h-3 w-3" />
                </button>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Show different banners based on auth status */}
        {currentUser ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-900 mb-2">Profile System Update</h3>
            <p className="text-blue-800 text-sm">
              Existing profiles remain free to edit and manage. New profiles require a one-time $5.00 payment to help maintain quality and support the platform.
            </p>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-green-900 mb-2">Browse Job Seekers</h3>
            <p className="text-green-800 text-sm">
              View profiles of skilled ranch workers looking for opportunities. 
              <a href="/signup" className="font-medium underline ml-1">Sign up</a> to create your own profile ($5.00 required).
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-800">
            <p>{error.message}</p>
          </div>
        ) : filteredProfiles.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500">
              {selectedState || selectedJobType
                ? 'No job seekers match your search criteria. Try adjusting your filters.'
                : 'No job seeker profiles available at the moment.'}
            </p>
            {(selectedState || selectedJobType) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="mt-4"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <JobSeekerGrid
            profiles={filteredProfiles}
            onCreateProfile={() => {
              if (currentUser) {
                setShowCreateModal(true);
              } else {
                // Redirect to signup if not authenticated
                window.location.href = '/signup';
              }
            }}
            onEditProfile={setEditingProfile}
            onDeleteProfile={handleDeleteProfile}
            currentUserId={currentUser?.id || currentUser?.uid}
            showCreateButton={!currentUser || !userHasProfile}
          />
        )}

        {showCreateModal && currentUser && (
          <CreateProfileModal
            onClose={() => {
              setShowCreateModal(false);
            }}
            onSubmit={handleCreateProfile}
          />
        )}

        {editingProfile && currentUser && (
          <EditProfileModal
            profile={editingProfile}
            onClose={() => setEditingProfile(null)}
            onSubmit={handleUpdateProfile}
          />
        )}
      </div>
    </>
  );
}